import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    filterBalances,
    getDateTextPicker,
    getStatus,
    getStatusCodes,
    useJunoCommonFunctions,
} from '../../../client/Juno/helpers';
import { CryptoTransactionTypes, TransactionTypes } from '../../../client/Juno/client/constant';
import { cryptoCurrencyList, fiatCurrencyList, statusOptions } from '../../helpers/constant';
import ActiveFilters from './ActiveFilters';
import FilterPanel from './FilterPanel';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import JunoButton from '../common/JunoButton';
import PropTypes from 'prop-types';
import { DatePicker } from '../TransactionOverview';

export default function FilterWrapper({
    title = 'TRANSACTION HISTORY',
    onFiltersChange,
    initialFilters = null,
    open,
    onClose,
    isDatepickerOpen = false,
    toggleDatepicker,
    selectedDateOption,
    handleDateChange
}) {
    // Redux selectors - same as original
    const clientData = useSelector(state => state.juno.clientData);
    const { useWindowWidth } = useJunoCommonFunctions();
    const windowWidth = useWindowWidth();
    const wrapperRef = useRef(null);

    useOutsideClick(wrapperRef, () => {
        if (showFilter) setShowFilter(false);
    });

    // Updated default filters to match original structure
    const defaultFilters = {
        currency: [],
        currencyType: [],
        type: [],
        from: '',
        to: '',
        status: [],
    };

    const [showFilter, setShowFilter] = useState(false);

    const [appliedFilters, setAppliedFilters] = useState(initialFilters || defaultFilters);

    const getCurrencyTypes = () => {
        const types = [];
        if (clientData?.cryptoTab === 'Enable') {
            types.push('Crypto');
        }
        if (clientData?.fiatTab === 'Enable') {
            types.push('Fiat');
        }
        return types;
    };

    const getTransactionTypes = () => {
        let options = [];

        if (appliedFilters?.currencyType[0] === 'Crypto' && clientData?.cryptoTab === 'Enable') {
            options = [...CryptoTransactionTypes];
        } else if (appliedFilters?.currencyType[0] === 'Fiat' && clientData?.fiatTab === 'Enable') {
            options = [...TransactionTypes];
        } else {
            if (clientData?.cryptoTab === 'Enable' && clientData?.fiatTab === 'Enable') {
                options = [...TransactionTypes, ...CryptoTransactionTypes];
            } else if (clientData?.cryptoTab === 'Enable') {
                options = [...CryptoTransactionTypes];
            } else if (clientData?.fiatTab === 'Enable') {
                options = [...TransactionTypes];
            }
        }

        return options;
    };

    const filteredCurrencyList = (() => {
        if (appliedFilters?.currencyType[0] === 'Crypto') {
            return clientData?.cryptoTab === 'Enable' ? cryptoCurrencyList : [];
        }

        if (appliedFilters?.currencyType[0] === 'Fiat') {
            return clientData?.fiatTab === 'Enable' ? fiatCurrencyList : [];
        }

        if (clientData?.cryptoTab === 'Enable' && clientData?.fiatTab === 'Enable') {
            return [...fiatCurrencyList, ...cryptoCurrencyList];
        }

        if (clientData?.cryptoTab === 'Enable') {
            return cryptoCurrencyList;
        }

        if (clientData?.fiatTab === 'Enable') {
            return fiatCurrencyList;
        }

        return [];
    })();

    const currencyListOptions = filteredCurrencyList?.map(x => x.symbol).filter(Boolean) || [];

    useEffect(() => {
        if (onFiltersChange) {
            onFiltersChange(appliedFilters);
        }
    }, [appliedFilters, onFiltersChange]);

    const handleApplyFilters = filters => {
        setAppliedFilters(filters);
        setShowFilter(false);
        setTimeout(() => {
            onClose();
        }, 100);
    };

    const handleClearFilters = () => {
        setAppliedFilters(defaultFilters);
    };

    const handleRemoveFilter = key => {
        const newFilters = { ...appliedFilters };

        if (key === 'from' || key === 'to') {
            newFilters.from = '';
            newFilters.to = '';
        } else if (key === 'status') {
            newFilters.status = [];
            newFilters.statusDisplay = [];
        } else if (Array.isArray(newFilters[key])) {
            newFilters[key] = [];
        } else {
            newFilters[key] = '';
        }

        setAppliedFilters(newFilters);
    };

    const filterOptions = {
        currencyTypes: getCurrencyTypes(),
        currencies: currencyListOptions,
        currencyIcons: filteredCurrencyList.reduce((acc, curr) => {
            acc[curr.symbol] = curr.icon;
            return acc;
        }, {}),
        transactionTypes: getTransactionTypes(),
        statusOptions: statusOptions,
    };


    const displayText = getDateTextPicker({
        from: appliedFilters.from,
        to: appliedFilters.to,
    });

    if (open && windowWidth < 1100) {
        return (
            <FilterPanel
                onApply={handleApplyFilters}
                onClose={() => {
                    setShowFilter(false);
                }}
                currentFilters={appliedFilters}
                filterOptions={filterOptions}
                getStatus={getStatus}
                getStatusCodes={getStatusCodes}
                filteredCurrencyList={filteredCurrencyList}
            />
        );
    }
    return (
        <div className="w-full">
            <div className="flex justify-between items-center  lg:!px-6 lg:!py-4">
                <h2 className="text-sm font-semibold !text-primary">{title}</h2>
                <div ref={wrapperRef} className="relative flex gap-2 items-center justify-center">
                    <DatePicker
                        isOpen={isDatepickerOpen}
                        toggle={toggleDatepicker}
                        selectedKey={selectedDateOption?.key}
                        onChange={handleDateChange}
                    />
                    <JunoButton
                        type="secondary"
                        onClick={() => setShowFilter(prev => !prev)}
                        className="flex items-center justify-center leading-4 tracking-wide !gap-1 !px-12 !py-0 text-[12px] max-w-[75px] !font-semibold font-Inter  rounded-lg transition-colors bg-filterPannel-btn-active-bg !text-primary border-filterPannel-btn-active-border !h-8">
                        <FilterListIcon fontSize="small" className="!text-primary " />
                        FILTER
                    </JunoButton>

                    {showFilter && (
                        <div>
                            <FilterPanel
                                onApply={handleApplyFilters}
                                onClose={() => {
                                    setShowFilter(false);
                                }}
                                currentFilters={appliedFilters}
                                filterOptions={filterOptions}
                                getStatus={getStatus}
                                getStatusCodes={getStatusCodes}
                                filteredCurrencyList={filteredCurrencyList}
                            />
                        </div>
                    )}
                </div>
            </div>

            <ActiveFilters
                filters={appliedFilters}
                onClear={handleClearFilters}
                onRemoveFilter={handleRemoveFilter}
                getStatus={getStatus}
                displayText={displayText}
            />
        </div>
    );
}

FilterWrapper.propTypes = {
    title: PropTypes.string,
    onFiltersChange: PropTypes.func,
    initialFilters: PropTypes.shape({
        currency: PropTypes.arrayOf(PropTypes.string),
        currencyType: PropTypes.arrayOf(PropTypes.string),
        type: PropTypes.arrayOf(PropTypes.string),
        from: PropTypes.string,
        to: PropTypes.string,
        status: PropTypes.arrayOf(PropTypes.string),
    }),
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

FilterWrapper.defaultProps = {
    title: 'TRANSACTION HISTORY',
    initialFilters: null,
};
